<script setup lang="ts">
import type {Story} from "~/types/story";

const {t} = useI18n()
const route = useRoute()
const {addToast} = useToasts()
const localeStore = useLanguageStore();
const config = useRuntimeConfig();

useHead({
  title: t('main.pageTitle'),
  link: [
    {
      rel: 'icon',
      type: 'image/png',
      href: '/assets/img/logo.png'
    },
    {
      rel: 'canonical',
      href: `https://www.InTheirLovingMemory.com${route.path}`
    }
  ],
  meta: [
  ]
})


const templateImages = [
  '/assets/img/templates/Template1.png',
  '/assets/img/templates/Template3.png',
  '/assets/img/templates/Template5.png',
  '/assets/img/templates/Template2.png',
];
const features = [
  {header: 'main.sections.stories.header1', img: '/assets/img/person1.webp', description: 'main.sections.stories.description1', reversed: false},
  {header: 'main.sections.stories.header2', img: '/assets/img/person2.webp', description: 'main.sections.stories.description2', reversed: true},
  {header: 'main.sections.stories.header3', img: '/assets/img/person3.webp', description: 'main.sections.stories.description3', reversed: false},
];

// Showcase stories
const featuredStories = computed<Story[]>(() => {
  const code = localeStore.selected.region
  let stories = (showcaseStories[code as keyof typeof showcaseStories] || genericShowcaseStories) as Story[];
  return stories;
})

const extensions = AllProducts.values().filter(item => item.type === 'Extension').toArray()
const card = AllProducts.values().filter(item => item.type === 'QRCodeEngraved').toArray().at(0)
const engravedCardPrice = computed(() => {
  if (card) {
    return roundUpToTwoDecimals(card.prices[localeStore.selected.currency] / 100);
  }
  return '';
})
import {genericShowcaseStories, showcaseStories} from "~/utils/showcaseStories";

// < ---- Start Swiper setup ---- >
import {Swiper, SwiperSlide} from 'swiper/vue';
import {Autoplay, EffectFade} from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/effect-fade';

const modules = [Autoplay, EffectFade];
// < ---- End Swiper setup ---- >


// Form handling
const formData = reactive({
  name: '',
  email: '',
  subject: '',
  message: ''
});
const isSendingContactForm = ref(false);

async function handleContactSubmit() {
  isSendingContactForm.value = true;
  console.debug("Sending form...");

  try {
    const response = await $fetch('/api/contact', {
      baseURL: config.public.apiBase,
      method: "POST",
      body: {...formData}
    });

    addToast(t('main.sections.contact.success.title'), t('main.sections.contact.success.content'), 'info');
    // Reset form on success
    Object.assign(formData, {name: '', email: '', subject: '', message: ''});
  } catch (error) {
    console.error(error);
    addToast(t('main.sections.contact.failure.title'), t('main.sections.contact.failure.content'), 'warning');
  } finally {
    isSendingContactForm.value = false;
  }
}

</script>

<template>
  <!-- Hero Section -->
  <section id="hero" class="hero section dark-background">
    <div class="container">
      <div class="row">
        <div class="col-lg-6 order-2 order-lg-1 d-flex flex-column justify-content-center">
          <h1>{{ $t('main.hero.title') }}</h1>
          <p>{{ $t('main.hero.description') }}</p>
          <div class="d-flex">
            <RouterLink to="/stories" class="btn-get-started">{{ $t('main.hero.callToAction') }}</RouterLink>
          </div>
        </div>
        <div class="col-lg-6 order-1 order-lg-2 hero-img d-flex flex-column justify-content-center align-items-center">
          <div class="swiper-container">
            <Swiper
                class="swiper-wrapper"
                :effect="'fade'"
                :fade-effect="{
                  crossFade: true,
                }"
                :direction="'horizontal'"
                :modules="modules"
                :navigation="{enabled: true}"
                :autoplay="{delay: 3000}"
            >
              <SwiperSlide v-for="image in templateImages" :key="image">
                <div class="swiper-slide ">
                  <img :src="image" class="rounded-top-5" alt="">
                </div>

              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </div>
    </div>
  </section><!-- /Hero Section -->

  <!-- Features Section -->
  <section id="features" class="features section">
    <!-- Section Title -->
    <div class="container section-title">
      <h2>{{ $t('main.sections.stories.title') }}</h2>
    </div><!-- End Section Title -->
    <div class="container">
      <div v-for="item in features" class="row mt-md-0 mt-5 align-items-center features-item p-0">
        <div class="col-md-4 col-lg-3 d-flex align-items-center justify-content-center" :class="{'order-1 order-md-2': item.reversed}">
          <img :src="item.img" class="img-pill" alt="">
        </div>
        <div class="col-md-8 col-lg-9 mt-md-0 mt-5" :class="{'order-2 order-md-1': item.reversed}">
          <h3>{{ $t(item.header) }}</h3>
          <p>{{ $t(item.description) }}</p>
        </div>
      </div>
    </div>
  </section><!-- /Features Section -->

  <!-- How It Works Section -->
  <section id="how-it-works" class="how-it-works section">
    <div class="container section-title">
      <h2>{{ $t('main.sections.howItWorks.title') }}</h2>
      <p>{{ $t('main.sections.howItWorks.description') }}</p>
    </div>

    <div class="container how-to-container">
      <HowToCard
          :title="$t('main.sections.howItWorks.step1.title')"
          :subtitle="$t('main.sections.howItWorks.step1.subtitle')"
          imageSrc="/assets/img/how-to-step1.png"
      >
        <ul>
          <li class="step">{{ $t('main.sections.howItWorks.step1.bullet1') }}</li>
          <li class="step">{{ $t('main.sections.howItWorks.step1.bullet2') }}</li>
          <li class="step">{{ $t('main.sections.howItWorks.step1.bullet3') }}</li>
        </ul>
      </HowToCard>
      <HowToCard
          :title="$t('main.sections.howItWorks.step2.title')"
          :subtitle="$t('main.sections.howItWorks.step2.subtitle')"
          imageSrc="/assets/img/how-to-step2.png"
          reversed
      >
        <ul>
          <li class="step">{{ $t('main.sections.howItWorks.step2.bullet1') }}</li>
          <li class="step"><span>{{ $t('main.sections.howItWorks.step2.bullet2') }}</span>
            <ul>
              <li v-for="extension in extensions">
                {{ $t(`cartItems.Extension.duration.${extension.properties.Duration}`) + ' ' + $t('main.sections.howItWorks.step2.for') }}<span
                  class="text-warning">{{ extension.prices[localeStore.selected.currency] / 100 }} {{ localeStore.selected.currency }}</span>
              </li>
            </ul>
          </li>
          <li class="step">{{ $t('main.sections.howItWorks.step2.bullet3') }}</li>
          <li class="step">{{ $t('main.sections.howItWorks.step2.bullet4') }}</li>
        </ul>
      </HowToCard>
      <HowToCard
          :title="$t('main.sections.howItWorks.step3.title')"
          :subtitle="$t('main.sections.howItWorks.step3.subtitle')"
          imageSrc="/assets/img/cards/Photo-Black.webp"
      >
        <ul>
          <li>{{ $t('main.sections.howItWorks.step3.bullet0') }}</li>
          <li class="step">
            <span>{{ $t('main.sections.howItWorks.step3.bullet1') }}</span>
            <ul>
              <li>{{ $t('main.sections.howItWorks.step3.bullet1_1') }}</li>
              <li>{{ $t('main.sections.howItWorks.step3.bullet1_2') }}</li>
              <li>{{ $t('main.sections.howItWorks.step3.bullet1_3') }}</li>
              <li>{{ $t('main.sections.howItWorks.step3.bullet1_4') }}</li>
            </ul>
          </li>
          <li class="step"><span v-html="$t('main.sections.howItWorks.step3.bullet2.prefix')"></span><span class="text-warning">{{ engravedCardPrice }} {{
              localeStore.selected.currency
            }}</span><span>{{ $t('main.sections.howItWorks.step3.bullet2.suffix') }}</span>
          </li>
        </ul>
      </HowToCard>
    </div>
  </section><!-- How It Works Section -->

  <!-- Story Showcase Section -->
  <section class="story-showcase max-width">
    <!-- Section Title -->
    <div class="container section-title">
      <h2>{{ $t('main.sections.howItLooks.title') }}</h2>
      <p>{{ $t('main.sections.howItLooks.description') }}</p>
    </div>

    <div class="container">
      <div class="row d-flex justify-content-center align-items-center">
        <StoryCard v-for="story in featuredStories"
                   :story="story"
                   :is-showcase="true"
        >
        </StoryCard>
      </div>
      <div class="text-center mt-5" v-html="$t('main.sections.howItLooks.disclaimer')"></div>
    </div>
  </section> <!-- Story Showcase Section -->

  <!--  Gift Cards Section-->
  <section id="gift-cards" class="section max-width gift-cards">
    <!-- Section Title -->
    <div class="container section-title">
      <h2>{{ $t('main.sections.giftCards.title') }}</h2>
    </div><!-- End Section Title -->
    <div class="container">
      <div class="row mt-0 align-items-center features-item">
        <div class="col-md-4 col-lg-3 d-flex align-items-center justify-content-center">
          <img src="/assets/img/kindness.webp" class="img-pill" alt="">
        </div>
        <div class="col-md-8 col-lg-9 pt-md-0 pt-4">
          <h3>{{ $t('main.sections.giftCards.subsection1.title') }}</h3>
          <p>{{ $t('main.sections.giftCards.subsection1.description') }}</p>
        </div>
      </div>
      <div class="row mt-0 align-items-center features-item">
        <div class="col-md-4 col-lg-3 order-1 order-md-2 d-flex align-items-center justify-content-center"
        >
          <img src="/assets/img/checklist-pencil.webp" class="img-pill" alt="">
        </div>
        <div class="col-md-8 col-lg-9 order-2 order-md-1 pt-lg-0 pt-4">
          <h3>{{ $t('main.sections.giftCards.subsection2.title') }}</h3>
          <div>
            <ul>
              <li v-html="$t('main.sections.giftCards.subsection2.step1')"></li>
              <li v-html="$t('main.sections.giftCards.subsection2.step2')"></li>
              <li v-html="$t('main.sections.giftCards.subsection2.step3')"></li>
              <li v-html="$t('main.sections.giftCards.subsection2.step4')"></li>
            </ul>
          </div>
        </div>
      </div><!-- Features Item -->

      <div class="row d-flex align-items-center justify-content-center pt-3">
        <NuxtLinkLocale to="order-giftcard" class="col-10 col-md-4 text-center btn btn-warning">{{ $t('main.sections.giftCards.button') }}</NuxtLinkLocale>
      </div>
    </div>
  </section><!--  Gift Cards Section-->

  <!-- FAQ Section -->
  <section id="faq" class="faq section max-width">
    <div class="container section-title">
      <h2>{{ $t('main.sections.faq.title') }}</h2>
    </div>
    <div class="container">
      <div class="row faq-item" v-for="n in 9" :key="n">
        <div class="col-lg-5 d-flex">
          <i class="bi bi-question-circle"></i>
          <h4>{{ $t(`main.sections.faq.q${n}`) }}</h4>
        </div>
        <div class="col-lg-7">
          <p>{{ $t(`main.sections.faq.a${n}`) }}</p>
        </div>
      </div><!-- End F.A.Q Item-->
    </div>
  </section> <!-- FAQ Section -->

  <!-- About Section -->
  <section id="about" class="about section max-width">
    <!-- Section Title -->
    <div class="container section-title">
      <h2>{{ $t('main.sections.aboutUs.title') }}</h2>
    </div><!-- End Section Title -->

    <div class="container">

      <div class="row gy-4">

        <div class="col-lg-6 position-relative order-lg-last">
          <img src="/assets/img/about.jpg" class="img-fluid" alt="">
        </div>

        <div class="col-lg-6 content">
          <p>{{ $t('main.sections.aboutUs.mainContent') }}</p>
          <ul>
            <li>
              <i class="bi bi-diagram-3"></i>
              <div>
                <p>{{ $t('main.sections.aboutUs.point1') }}</p>
              </div>
            </li>
            <li>
              <i class="bi bi-fullscreen-exit"></i>
              <div>
                <p>{{ $t('main.sections.aboutUs.point2') }}</p>
              </div>
            </li>
            <li>
              <i class="bi bi-broadcast"></i>
              <div>
                <p>{{ $t('main.sections.aboutUs.point3') }}</p>
              </div>
            </li>
          </ul>
        </div>

      </div>

    </div>

  </section><!-- /About Section -->

  <!-- Contact Section -->
  <section id="contact" class="contact section">

    <!-- Section Title -->
    <div class="container section-title">
      <h2>{{ $t('main.sections.contact.title') }}</h2>
      <p>{{ $t('main.sections.contact.description') }}</p>
    </div><!-- End Section Title -->

    <div class="container">

      <div class="row gy-4 align-items-center justify-content-center">
        <div class="col-lg-8">
          <form class="email-form mb-4 needs-validation" @submit.prevent="handleContactSubmit">
            <div class="row gy-4">
              <div class="col-md-6 input-group-custom">
                <input v-model="formData.name" type="text" id="form-name" name="name" class="input-field" placeholder=" " required>
                <label for="form-name" class="input-label">{{ $t('main.sections.contact.name') }}</label>
              </div>

              <div class="col-md-6 input-group-custom">
                <input v-model="formData.email" type="email" class="input-field" id="form-email" name="email" placeholder=" " required>
                <label for="form-email" class="input-label">{{ $t('main.sections.contact.email') }}</label>
              </div>

              <div class="col-md-12 input-group-custom">
                <input v-model="formData.subject" type="text" class="input-field" id="form-subject" name="subject" placeholder=" " required>
                <label for="form-subject" class="input-label">{{ $t('main.sections.contact.subject') }}</label>
              </div>

              <div class="col-md-12 input-group-custom textarea-wrapper">
                <textarea v-model="formData.message" class="input-field" id="form-message" name="message" rows="4" placeholder=" " required></textarea>
                <label for="form-message" class="input-label">{{ $t('main.sections.contact.message') }}</label>
              </div>

              <div class="col-md-12 text-center">
                <LoadingButton :label="$t('main.sections.contact.sendMessage')"
                               :loading="isSendingContactForm"
                               type="submit"
                />
              </div>

            </div>
          </form>
        </div><!-- End Contact Form -->

      </div>

    </div>

  </section><!-- /Contact Section -->

</template>

<style scoped>
/*--------------------------------------------------------------
# Hero Section
--------------------------------------------------------------*/
.hero {
  width: 100%;
  min-height: fit-content;
  height: 100vh;
  position: relative;
  padding: 120px 0 60px 0;
  display: flex;
  align-items: center;
}

.hero h1 {
  margin: 0;
  font-size: 48px;
  font-weight: 700;
  line-height: 56px;
  color: color-mix(in srgb, var(--heading-color), transparent 20%);
}

.hero p {
  color: color-mix(in srgb, var(--default-color), transparent 30%);
  margin: 5px 0 30px 0;
  font-size: 20px;
  font-weight: 400;
}

.hero .btn-get-started {
  color: var(--contrast-color);
  background: var(--accent-color);
  font-family: var(--heading-font), serif;
  font-weight: 400;
  font-size: 15px;
  letter-spacing: 1px;
  display: inline-block;
  padding: 10px 28px 12px 28px;
  border-radius: 50px;
  transition: 0.5s;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
}

.hero .btn-get-started:hover {
  color: var(--contrast-color);
  background: color-mix(in srgb, var(--accent-color), transparent 15%);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.1);
}

.hero .animated {
  animation: up-down 2s ease-in-out infinite alternate-reverse both;
}

@media (max-width: 640px) {
  .hero h1 {
    font-size: 28px;
    line-height: 36px;
  }

  .hero p {
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 30px;
  }

  .hero .btn-get-started,
  .hero .btn-watch-video {
    font-size: 13px;
  }
}

@keyframes up-down {
  0% {
    transform: translateY(10px);
  }

  100% {
    transform: translateY(-10px);
  }
}

/*--------------------------------------------------------------
# Global Sections
--------------------------------------------------------------*/

/*--------------------------------------------------------------
# Features Section
--------------------------------------------------------------*/
.features {
  max-width: var(--max-width-main);
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
  align-self: center;
  justify-self: center;
}

.features p {
  text-align: justify;
}

.features .features-item {
  color: color-mix(in srgb, var(--default-color), transparent 20%);
}

.features .features-item + .features-item {
  margin-top: 50px;
}

@media (max-width: 640px) {
  .features .features-item + .features-item {
    margin-top: 40px;
  }
}

.gift-cards .features-item h3,
.features .features-item h3 {
  font-weight: 700;
  font-size: 26px;
}

.features .features-item ul {
  list-style: none;
  padding: 0;
}

.features .features-item ul li {
  padding-bottom: 10px;
  display: flex;
  align-items: center;
}

.features .features-item ul li:last-child {
  padding-bottom: 0;
}

.features .features-item ul i {
  font-size: 20px;
  padding-right: 4px;
  color: var(--accent-color);
}

.features .features-item p:last-child {
  margin-bottom: 0;
}

.img-pill {
  height: 300px;
  max-height: 300px;
  max-width: 200px;
  width: 100%;
  border-radius: 100px;
  border: 3px solid color-mix(in srgb, var(--accent-color), transparent 30%);
  object-fit: cover;
}

/*--------------------------------------------------------------
# Faq Section
--------------------------------------------------------------*/
.faq .faq-item {
  margin: 20px 0;
  padding: 20px 0;
  border-bottom: 1px solid color-mix(in srgb, var(--default-color), transparent 85%);
}

.faq .faq-item i {
  color: var(--accent-color);
  font-size: 18px;
  margin-right: 10px;
}

.faq .faq-item h4 {
  font-size: 16px;
  line-height: 26px;
  font-weight: 700;
}

.faq .faq-item p {
  font-size: 15px;
}

.faq .faq-item:first-child {
  padding-top: 0;
  margin-top: 0;
}

.faq .faq-item:last-child {
  border: 0;
  padding-bottom: 0;
  margin-bottom: 0;
}

/*--------------------------------------------------------------
# About Section
--------------------------------------------------------------*/
.about .content h3 {
  font-size: 2rem;
  font-weight: 700;
}

.about .content ul {
  list-style: none;
  padding: 0;
}

.about .content ul li {
  display: flex;
  align-items: flex-start;
  margin-top: 40px;
}

.about .content ul i {
  flex-shrink: 0;
  font-size: 48px;
  color: var(--accent-color);
  margin-right: 20px;
  line-height: 0;
}

.about .content ul h5 {
  font-size: 18px;
  font-weight: 700;
}

.about .content ul p {
  font-size: 15px;
}

.about .content p:last-child {
  margin-bottom: 0;
}

.about .pulsating-play-btn {
  position: absolute;
  left: calc(50% - 47px);
  top: calc(50% - 47px);
}

.contact .email-form {
  background-color: var(--surface-color);
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  height: 100%;
  padding: 30px;
}

@media (max-width: 575px) {
  .contact .email-form {
    padding: 20px;
  }
}

.contact .email-form button[type=submit] {
  color: var(--contrast-color);
  background: var(--accent-color);
  border: 0;
  padding: 10px 30px;
  transition: 0.4s;
  border-radius: 50px;
}

.contact .email-form button[type=submit]:hover {
  background: color-mix(in srgb, var(--accent-color), transparent 20%);
}

.hero-img {
  .swiper-container {
    overflow: hidden;
    width: 100%;
    height: 100%;
    max-width: 400px;
  }

  .image-container {
    width: 100%;
    max-width: 400px;
    height: 600px; /* Show only 400px height */
    overflow: hidden;
    position: relative;
  }

  .image-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    /*animation: scrollImage 30s linear infinite;*/
    /*animation-delay: 3s;*/
  }

  img {
    width: 100%;
    object-fit: cover;
  }
}
</style>